/*
 * hyperion-index:begin
 * ---
 * path: hyperion-cloud/web/src/capabilities/compliance-assessment/components/ComplianceSkeletons.tsx
 * layer: web
 * area: compliance
 * kind: feature
 * web_root: features
 * relpath: web/src/capabilities/compliance-assessment/components/ComplianceSkeletons.tsx
 * filename: ComplianceSkeletons.tsx
 * extension: tsx
 * ---
 * hyperion-index:end
 */

/**
 * Sprint 338: Compliance Skeleton Components
 * Loading state placeholders for all compliance views
 */

import { HTMLAttributes } from 'react';
import { clsx } from 'clsx';

import { Card, CardContent, CardHeader } from '@/platform/design-system/components/ui/Card';

// Base skeleton pulse animation
const pulseClass = 'motion-loading-pulse bg-slate-200 dark:bg-slate-700 rounded';

// ============================================================================
// Dashboard Skeleton
// ============================================================================

export const ComplianceDashboardSkeleton = ({
  className,
  ref,
  ...props
}: HTMLAttributes<HTMLDivElement> & { ref?: React.Ref<HTMLDivElement> }) => (
  <div ref={ref} className={clsx('p-6 space-y-6', className)} {...props}>
    {/* Header */}
    <div className="flex justify-between items-center">
      <div className={clsx(pulseClass, 'h-8 w-48')} />
      <div className="flex gap-2">
        <div className={clsx(pulseClass, 'h-10 w-32')} />
        <div className={clsx(pulseClass, 'h-10 w-36')} />
      </div>
    </div>

    {/* Score cards */}
    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
      {[...Array(4)].map((_, i) => (
        <Card key={i}>
          <CardHeader className="pb-2">
            <div className={clsx(pulseClass, 'h-4 w-24')} />
          </CardHeader>
          <CardContent>
            <div className="flex flex-col items-center">
              <div className={clsx(pulseClass, 'h-32 w-32 rounded-full')} />
              <div className={clsx(pulseClass, 'h-4 w-20 mt-2')} />
            </div>
          </CardContent>
        </Card>
      ))}
    </div>

    {/* Trend chart */}
    <Card>
      <CardHeader>
        <div className={clsx(pulseClass, 'h-5 w-48')} />
      </CardHeader>
      <CardContent>
        <div className={clsx(pulseClass, 'h-[300px] w-full')} />
      </CardContent>
    </Card>

    {/* Two column layout */}
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <Card>
        <CardHeader>
          <div className={clsx(pulseClass, 'h-5 w-40')} />
        </CardHeader>
        <CardContent className="space-y-3">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="flex items-center gap-2">
              <div className={clsx(pulseClass, 'h-3 flex-1')} />
              <div className={clsx(pulseClass, 'h-4 w-12')} />
            </div>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className={clsx(pulseClass, 'h-5 w-36')} />
        </CardHeader>
        <CardContent className="space-y-3">
          {[...Array(4)].map((_, i) => (
            <div key={i} className={clsx(pulseClass, 'h-20 w-full')} />
          ))}
        </CardContent>
      </Card>
    </div>

    {/* Tenant grid */}
    <Card>
      <CardHeader>
        <div className={clsx(pulseClass, 'h-5 w-44')} />
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          <div className={clsx(pulseClass, 'h-10 w-full')} />
          {[...Array(5)].map((_, i) => (
            <div key={i} className={clsx(pulseClass, 'h-14 w-full')} />
          ))}
        </div>
      </CardContent>
    </Card>
  </div>
);

// ============================================================================
// Control List Skeleton
// ============================================================================

export const ControlListSkeleton = ({
  className,
  ref,
  ...props
}: HTMLAttributes<HTMLDivElement> & { ref?: React.Ref<HTMLDivElement> }) => (
  <div ref={ref} className={clsx('p-6 space-y-4', className)} {...props}>
    {/* Header */}
    <div className="flex justify-between items-center">
      <div>
        <div className={clsx(pulseClass, 'h-8 w-48 mb-2')} />
        <div className={clsx(pulseClass, 'h-4 w-64')} />
      </div>
      <div className={clsx(pulseClass, 'h-10 w-40')} />
    </div>

    {/* Filters */}
    <div className="flex gap-4 flex-wrap">
      <div className={clsx(pulseClass, 'h-10 w-64')} />
      <div className={clsx(pulseClass, 'h-10 w-32')} />
      <div className={clsx(pulseClass, 'h-10 w-40')} />
      <div className={clsx(pulseClass, 'h-10 w-32')} />
    </div>

    {/* Table */}
    <div className="border rounded-md">
      {/* Header row */}
      <div className="flex items-center gap-4 p-4 border-b bg-slate-50 dark:bg-slate-800">
        <div className={clsx(pulseClass, 'h-4 w-4')} />
        <div className={clsx(pulseClass, 'h-4 w-24')} />
        <div className={clsx(pulseClass, 'h-4 w-20')} />
        <div className={clsx(pulseClass, 'h-4 w-20')} />
        <div className={clsx(pulseClass, 'h-4 flex-1')} />
        <div className={clsx(pulseClass, 'h-4 w-16')} />
        <div className={clsx(pulseClass, 'h-4 w-24')} />
        <div className={clsx(pulseClass, 'h-4 w-24')} />
      </div>
      {/* Data rows */}
      {[...Array(10)].map((_, i) => (
        <div key={i} className="flex items-center gap-4 p-4 border-b last:border-b-0">
          <div className={clsx(pulseClass, 'h-4 w-4')} />
          <div className={clsx(pulseClass, 'h-4 w-28')} />
          <div className={clsx(pulseClass, 'h-6 w-12 rounded-full')} />
          <div className={clsx(pulseClass, 'h-4 w-24')} />
          <div className={clsx(pulseClass, 'h-4 flex-1 max-w-md')} />
          <div className={clsx(pulseClass, 'h-6 w-16 rounded-full')} />
          <div className={clsx(pulseClass, 'h-6 w-24 rounded-full')} />
          <div className={clsx(pulseClass, 'h-4 w-20')} />
        </div>
      ))}
    </div>
  </div>
);

// ============================================================================
// Control Detail Skeleton
// ============================================================================

export const ControlDetailSkeleton = ({
  className,
  ref,
  ...props
}: HTMLAttributes<HTMLDivElement> & { ref?: React.Ref<HTMLDivElement> }) => (
  <div ref={ref} className={clsx('p-6 space-y-6', className)} {...props}>
    {/* Header */}
    <div className="flex justify-between items-start">
      <div>
        <div className="flex items-center gap-2 mb-2">
          <div className={clsx(pulseClass, 'h-6 w-12 rounded-full')} />
          <div className={clsx(pulseClass, 'h-4 w-4')} />
          <div className={clsx(pulseClass, 'h-4 w-32')} />
        </div>
        <div className={clsx(pulseClass, 'h-8 w-48 mb-2')} />
        <div className={clsx(pulseClass, 'h-5 w-96')} />
      </div>
      <div className="flex items-center gap-4">
        <div className={clsx(pulseClass, 'h-8 w-24 rounded-full')} />
        <div className="flex gap-2">
          <div className={clsx(pulseClass, 'h-10 w-28')} />
          <div className={clsx(pulseClass, 'h-10 w-28')} />
        </div>
      </div>
    </div>

    {/* Info cards */}
    <div className="grid grid-cols-4 gap-4">
      {[...Array(4)].map((_, i) => (
        <Card key={i}>
          <CardHeader className="pb-2">
            <div className={clsx(pulseClass, 'h-4 w-20')} />
          </CardHeader>
          <CardContent>
            <div className={clsx(pulseClass, 'h-6 w-16')} />
          </CardContent>
        </Card>
      ))}
    </div>

    {/* Tabs */}
    <div className="space-y-4">
      <div className="flex gap-2 border-b">
        {[...Array(5)].map((_, i) => (
          <div key={i} className={clsx(pulseClass, 'h-10 w-24')} />
        ))}
      </div>

      {/* Tab content */}
      <Card>
        <CardHeader>
          <div className={clsx(pulseClass, 'h-5 w-32')} />
        </CardHeader>
        <CardContent className="space-y-4">
          <div className={clsx(pulseClass, 'h-4 w-full')} />
          <div className={clsx(pulseClass, 'h-4 w-full')} />
          <div className={clsx(pulseClass, 'h-4 w-3/4')} />
          <div className={clsx(pulseClass, 'h-4 w-full')} />
          <div className={clsx(pulseClass, 'h-4 w-5/6')} />
        </CardContent>
      </Card>
    </div>
  </div>
);

// ============================================================================
// Score Gauge Skeleton
// ============================================================================

export const ScoreGaugeSkeleton = ({
  className,
  ref,
  ...props
}: HTMLAttributes<HTMLDivElement> & { ref?: React.Ref<HTMLDivElement> }) => (
  <div ref={ref} className={clsx('flex flex-col items-center', className)} {...props}>
    <div className={clsx(pulseClass, 'w-32 h-32 rounded-full')} />
    <div className={clsx(pulseClass, 'h-4 w-20 mt-2')} />
  </div>
);

// ============================================================================
// Findings List Skeleton
// ============================================================================

export const FindingsListSkeleton = ({
  className,
  ref,
  ...props
}: HTMLAttributes<HTMLDivElement> & { ref?: React.Ref<HTMLDivElement> }) => (
  <div ref={ref} className={clsx('space-y-3', className)} {...props}>
    {[...Array(4)].map((_, i) => (
      <div key={i} className="border rounded-lg p-4 border-slate-200 dark:border-slate-700">
        <div className="flex items-start gap-3">
          <div className={clsx(pulseClass, 'w-5 h-5 rounded')} />
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <div className={clsx(pulseClass, 'h-5 w-24')} />
              <div className={clsx(pulseClass, 'h-5 w-16 rounded-full')} />
            </div>
            <div className={clsx(pulseClass, 'h-4 w-full mb-2')} />
            <div className={clsx(pulseClass, 'h-4 w-3/4')} />
            <div className="flex items-center gap-4 mt-3">
              <div className={clsx(pulseClass, 'h-3 w-24')} />
              <div className={clsx(pulseClass, 'h-3 w-20')} />
            </div>
          </div>
        </div>
      </div>
    ))}
  </div>
);

// ============================================================================
// Remediation Wizard Skeleton
// ============================================================================

export const RemediationWizardSkeleton = ({
  className,
  ref,
  ...props
}: HTMLAttributes<HTMLDivElement> & { ref?: React.Ref<HTMLDivElement> }) => (
  <div ref={ref} className={clsx('space-y-6', className)} {...props}>
    {/* Progress indicator */}
    <div className="flex items-center justify-between">
      {[...Array(4)].map((_, i) => (
        <div key={i} className="flex items-center">
          <div className={clsx(pulseClass, 'w-8 h-8 rounded-full')} />
          {i < 3 && <div className={clsx(pulseClass, 'w-16 h-px mx-2')} />}
        </div>
      ))}
    </div>

    {/* Content */}
    <div className="space-y-4">
      <div className={clsx(pulseClass, 'h-20 w-full rounded-lg')} />
      {[...Array(3)].map((_, i) => (
        <div
          key={i}
          className="flex items-center gap-2 p-3 rounded-md bg-slate-50 dark:bg-slate-800"
        >
          <div className={clsx(pulseClass, 'w-5 h-5 rounded-full')} />
          <div className="flex-1">
            <div className={clsx(pulseClass, 'h-4 w-32 mb-1')} />
            <div className={clsx(pulseClass, 'h-3 w-48')} />
          </div>
        </div>
      ))}
    </div>

    {/* Actions */}
    <div className="flex justify-end gap-2">
      <div className={clsx(pulseClass, 'h-10 w-24')} />
      <div className={clsx(pulseClass, 'h-10 w-24')} />
    </div>
  </div>
);

// ============================================================================
// Timeline Skeleton
// ============================================================================

export const TimelineSkeleton = ({
  className,
  ref,
  ...props
}: HTMLAttributes<HTMLDivElement> & { ref?: React.Ref<HTMLDivElement> }) => (
  <div ref={ref} className={clsx('space-y-4', className)} {...props}>
    {[...Array(5)].map((_, i) => (
      <div key={i} className="flex gap-4">
        <div className="flex flex-col items-center">
          <div className={clsx(pulseClass, 'w-3 h-3 rounded-full')} />
          {i < 4 && <div className={clsx(pulseClass, 'w-0.5 h-16')} />}
        </div>
        <div className="flex-1 pb-4">
          <div className="flex items-center gap-2 mb-2">
            <div className={clsx(pulseClass, 'h-5 w-24 rounded-full')} />
            <div className={clsx(pulseClass, 'h-4 w-32')} />
          </div>
          <div className={clsx(pulseClass, 'h-4 w-full mb-1')} />
          <div className={clsx(pulseClass, 'h-4 w-2/3')} />
        </div>
      </div>
    ))}
  </div>
);

export default {
  ComplianceDashboardSkeleton,
  ControlListSkeleton,
  ControlDetailSkeleton,
  ScoreGaugeSkeleton,
  FindingsListSkeleton,
  RemediationWizardSkeleton,
  TimelineSkeleton,
};
